/**
 * The fixture implementation of `EditorApi`. It is what the editor screen runs against today, and
 * what the accessibility leg scans.
 *
 * IT IS NOT A STUB THAT ALWAYS SUCCEEDS — the same design ./fixture-cabinet-api.ts states, and
 * on this surface it matters more, because three of the editor's panes exist ONLY on a refusal
 * path:
 *
 *   ★ `art_0192f4d3` CONFLICTS ON ITS FIRST AUTOSAVE. It models the case the contract built a
 *     whole response body for — a second tab saved while this one was typing — and the 409 carries
 *     the server's WHOLE working copy, so the conflict pane has two real documents to put side by
 *     side. After the author takes one, the re-based save succeeds. A fixture whose autosave always
 *     returned 200 would leave that pane unrenderable and therefore unchecked.
 *   ★ `art_0192f3ff` IS A SHORT DRAFT, so `submitArticle` refuses it with the WHOLE failure set,
 *     computed against the very rules `getStructuralRules` served rather than typed out here. A
 *     canned failure list would let the checklist and the refusal disagree.
 *   ★ `art_0192f5e2` IS `in_review`, so `writableBy` is `nobody` and every control is disabled.
 *     G19's three write rules are a state of the screen, not an error (authors-api.yaml).
 *   ★ `art_0192f5c9` IS PUBLISHED - the list's "The eclipse nobody will photograph" - so its page is
 *     the read view, and that page is where "Archive this article" now sits (design 24, T2).
 *   ★ A CLAIMED PICTURE IS STORED ON THIS DESK, so an autosave naming it as the cover ADOPTS it
 *     and re-derives `awaitingCover`, as the platform's does. Before, the save kept the opening
 *     cover whatever the draft named, so a new cover vanished on the next read and the short
 *     draft's checklist stayed red under a cover that met the rule.
 *   ★ THE SATURN PIECE AND THE PIECE IN REVIEW HAVE STORED VERSIONS; THE SHORT DRAFT HAS NONE. A
 *     version FIXES THE SOURCE LANGUAGE, as the platform's does (`applySourceLanguage`), so the
 *     short draft is the one whose language can still move - once - and a change on the other two
 *     is the platform's uncoded 422. The History pane and the language picker both read that.
 *
 * WHAT IT DELIBERATELY DOES NOT DO: it does not invent a field. Every value below is annotated with
 * a type derived from the generated contract through ./editor-api, so the mock cannot drift from
 * the spec without failing to compile.
 *
 * THE IDS ARE ./fixtures.ts'S OWN. `FIXTURE_ARTICLES` already carries one row per status
 * (fixtures.ts) and the article list links each row into this editor, so the two fixtures
 * describe one author's desk rather than two.
 */
import {
  FIXTURE_CALENDAR_EVENTS,
  FIXTURE_CALENDAR_GROUPS,
} from '../../calendar/api/fixture-calendar-api';
import { AutosaveConflict, StructuralLimitsRefusal, VIDEO_RULES } from './editor-api';
import { articleWordCount, countTitleLength } from './editor-measure';
import { FIXTURE_PAYMENTS, FIXTURE_SESSION } from './fixtures';
import {
  FIXTURE_NEW_STREAM_PREFIX,
  FIXTURE_NEW_VIDEO_PREFIX,
  createFixtureVideoDesk,
  emptyStreamDraft,
  emptyVideoDraft,
  seededVideoCopies,
  videoRulesOf,
} from './fixture-video-desk';
import { ApiProblem, NotFoundFailure, TransportFailure } from './problem';
import type {
  ArticleCalendarLink,
  ArticleCalendarLinks,
  ArticleCalendarLinksWrite,
  ArticleImageClaim,
  ArticleVersion,
  ArticleVersionSummary,
  Block,
  CategoryList,
  EditorApi,
  FindingResolved,
  OwnProfile,
  PreviewLink,
  PublishDateSuggestions,
  ReviewFinding,
  ReviewRecord,
  SourceLanguageList,
  StoredImage,
  StructuralLimitFailure,
  StructuralRules,
  TagSuggestionGroup,
  TagSuggestions,
  VideoFailureCode,
  VideoRule,
  WorkingCopy,
  WorkingCopySaved,
} from './editor-api';
import type { AutosaveRequest } from './editor-api';
import type { ErrorCode, Problem, PublicId, StreamAccess, TransitionResult } from './generated';

export interface FixtureEditorApiOptions {
  /** Milliseconds of pretend latency, so the loading states are visible rather than theoretical. */
  readonly latencyMs?: number;
  /** Every read fails at the transport. Proves the error state is readable and offers a retry. */
  readonly offline?: boolean;
  /** Every video conversion on this desk fails with this code (./fixture-video-desk.ts). */
  readonly videoFailure?: VideoFailureCode;
  /**
   * Whether staff let this author announce live streams (1.2.0). Granted unless a spec says so;
   * without it every write to a stream draft is refused, as the platform refuses it.
   */
  readonly streamAccess?: StreamAccess;
}

/** The longest a live stream may be planned for (`STREAM_LONGEST_MS` on the platform). */
const STREAM_LONGEST_MS = 24 * 60 * 60_000;

const DEFAULT_LATENCY_MS = 180;

/**
 * The desk's now, which a live stream's start must be after at submit: the instant the payments
 * read reports, the one the screens take as the platform's (no clock is read, C11).
 */
const DESK_NOW = FIXTURE_PAYMENTS.computedAt;

function problem(status: number, errorCode: ErrorCode, title: string, detail?: string): Problem {
  return {
    type: `https://moonx.art/problems/${errorCode.toLowerCase().replaceAll('_', '-')}`,
    title,
    status,
    error_code: errorCode,
    ...(detail === undefined ? {} : { detail }),
  };
}

/* ---------------------------------------------------------------------------------------------
 * The reference data. FETCHED BY THE SCREEN, NEVER HARDCODED IN IT.
 * ------------------------------------------------------------------------------------------- */

/**
 * D21's eight rules, at the numbers the corpus states: words 300 to 7,000, blocks 1 to 50, title
 * 10 to 70, subtitle 10 to 100, categories 1 to 5, at least one image including the cover, quote
 * 400 with attribution 100, cover at least 800 by 450 (design 24 B2-COVER; 1600 by 900 before, at
 * authors-api.yaml, docs/design/09-authors-app.md).
 *
 * ★ THE PROTOTYPE'S 600-WORD MINIMUM IS NOT ONE OF THEM and appears nowhere on this surface. The
 * whole reason this operation exists is that the editor mirrors the server's values "so client and
 * server cannot drift" (authors-api.yaml); a second copy of 300 living in a component would be
 * the drift itself.
 *
 * `message` is OMITTED on every row (`unit` is served since wave 23; unused here), the contract's state rather
 * than an economy here: nothing in the corpus writes the author-facing wording and
 * `structural_policy` has no prose column to hold one. The checklist supplies its own labels and
 * says so at its call site.
 */
const FIXTURE_STRUCTURAL_RULES: StructuralRules = {
  policyVersion: 'structural-limits/2026-07-01',
  wordCountDefinition:
    'Whitespace-split tokens over the title, the subtitle, every block’s plain text and each ' +
    'quote attribution, NFC-normalised. A bare web address is not a word; image captions are.',
  rules: [
    { rule: 'word_count', min: 300, max: 7_000 },
    { rule: 'block_count', min: 1, max: 50 },
    { rule: 'title_length', min: 10, max: 70 },
    { rule: 'subtitle_length', min: 10, max: 100 },
    { rule: 'category_count', min: 1, max: 5 },
    { rule: 'image_present', min: 1, max: null },
    // TWO BOUNDS UNDER ONE RULE NAME, exactly as `cover_dimensions` is two. `max` repeats
    // `bodyMax` so no client that already reads it changes behaviour.
    { rule: 'quote_length', max: 400, bodyMax: 400, attributionMax: 100 },
    { rule: 'cover_dimensions', minWidthPx: 800, minHeightPx: 450 },
  ],
};

/** ADMIN-managed reference data. The codes are stable and NOT all lower-case — never pattern-match. */
const FIXTURE_CATEGORIES: CategoryList = {
  items: [
    { code: 'astrology', label: 'Astrology', sortOrder: 1 },
    { code: 'tarot', label: 'Tarot', sortOrder: 2 },
    { code: 'numerology', label: 'Numerology', sortOrder: 3 },
    { code: 'lunarCalendar', label: 'Lunar calendar', sortOrder: 4 },
    { code: 'dreams', label: 'Dreams', sortOrder: 5 },
    { code: 'ritualsPractice', label: 'Rituals and practice', sortOrder: 6 },
    { code: 'relationships', label: 'Relationships', sortOrder: 7 },
    { code: 'selfKnowledge', label: 'Self-knowledge', sortOrder: 8 },
  ],
};

const FIXTURE_TAGS: TagSuggestions = {
  items: [
    { tagText: 'full moon', useCount: 214 },
    { tagText: 'retrograde', useCount: 188 },
    { tagText: 'ninth house', useCount: 41 },
    { tagText: 'eclipse season', useCount: 37 },
    { tagText: 'saturn return', useCount: 29 },
    { tagText: 'beginner guide', useCount: 22 },
  ],
};

/**
 * MoonX's own tag list, SHORTENED for this desk: a few groups of the office sheet
 * (docs/design/24-teammate-round-1/tags.tsv) and a few tags each, after the clean-ups design 24's
 * T6 block approves - "Natal chart" and "Zodiac" in Astrology; "Calendar", "Sky", "Star" and
 * "Planet" beside "NASA" in Astronomy; the rest of the ungrouped rows in "Other"; a capital on every
 * row; "Visualization"; no trailing space on "Taurus Season". Each group's tags come as the server
 * sends them: High, then Medium, then Low, then none, in the sheet's order within each.
 */
const FIXTURE_TAG_GROUPS: readonly TagSuggestionGroup[] = [
  {
    label: 'Moon',
    tags: [
      'Full moon',
      'New moon',
      'Lunar eclipse',
      'Solar eclipse',
      'Moon Phase',
      'Waxing moon',
      'Waning moon',
      'Eclipse season',
    ],
  },
  { label: 'Zodiac', tags: ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Fire signs'] },
  {
    label: 'Astrology',
    tags: [
      'Birth chart',
      'Rising sign',
      'Mercury retrograde',
      'Saturn retrograde',
      'Horoscope',
      'Transits',
      'Retrograde',
      'Venus retrograde',
      'Mars retrograde',
      'Jupiter retrograde',
      'Natal chart',
      'Zodiac',
    ],
  },
  {
    label: 'Tarot & divination',
    tags: ['Major arcana', 'Card meanings', 'Tarot spreads', 'Minor arcana'],
  },
  {
    label: 'Practice',
    tags: ['Ritual', 'Meditation', 'Manifestation', 'Journaling', 'Visualization'],
  },
  {
    label: 'Seasons & holidays',
    tags: ['Solstice', 'Equinox', 'Aries Season', 'Taurus Season'],
  },
  { label: 'Astronomy', tags: ['NASA', 'Calendar', 'Sky', 'Star', 'Planet'] },
  {
    label: 'Other',
    tags: ['Meaning', 'Palmistry', 'Dating', 'Angel numbers', 'Life path number', 'Soulmate'],
  },
];

const FIXTURE_PUBLISH_DATES: PublishDateSuggestions = {
  items: [
    { date: '2026-09-27', reason: 'New moon in Libra', calendarEventId: 'cev_0192c0a1' },
    { date: '2026-10-11', reason: 'Full moon in Aries', calendarEventId: 'cev_0192c0b4' },
    { date: '2026-10-25', reason: 'Editorial theme week: thresholds' },
  ],
};

/* ---------------------------------------------------------------------------------------------
 * The calendar links and the address check, refusing what the platform refuses, with its codes.
 * ------------------------------------------------------------------------------------------- */

/** One link as it is written: `{ calendarEventId, relation }` and nothing else. */
type CalendarLinkPair = ArticleCalendarLinksWrite['links'][number];

/**
 * The live rows of `calendar_link_relation` - `timed_to` and `about`, the two the contract names on
 * `ArticleCalendarLink.relation`. The platform checks a relation against that TABLE and answers 400
 * for any other (src/modules/editorial/cabinet/article-calendar-links.service.ts); so does this desk.
 */
const FIXTURE_LINK_RELATIONS: ReadonlySet<string> = new Set(['timed_to', 'about']);

/** `ArticleCalendarLinksWrite.links` declares `maxItems: 10`, on the SENT array. */
const MAX_CALENDAR_LINKS = 10;

/** `ArticleCalendarLinksWrite.groups` declares `maxItems: 5` (design 30). */
const MAX_CALENDAR_GROUPS = 5;

/** `checkArticleSlugAvailability`'s `slug`: 3 to 60 characters of this pattern, else the pipe's 400. */
const ARTICLE_SLUG_SHAPE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** The one address another piece holds on this desk: the check says so, and a save is refused it. */
const FIXTURE_TAKEN_SLUG = 'saturn-return';

/**
 * The statuses an article can only reach by having been published. No transition leads from any
 * of them back to `draft` (`article_transition_rule`, migration 0003), so an article in one of them
 * has the address D28 stamped at first publish - the platform's `addressFrozen`.
 */
const EVER_PUBLISHED: ReadonlySet<string> = new Set(['published', 'unpublished', 'archived']);

/* ---------------------------------------------------------------------------------------------
 * The documents.
 * ------------------------------------------------------------------------------------------- */

const COVER: StoredImage = {
  fileId: 'img_0192d41c',
  url: 'https://cdn.moonx.art/articles/0192d41c/cover.jpg',
  widthPx: 2_048,
  heightPx: 1_152,
  byteSize: 481_204,
  contentType: 'image/jpeg',
};

/** The Saturn piece's one body picture - its image block names this id (`SATURN_BLOCKS`). */
const SATURN_PICTURE: StoredImage = {
  fileId: 'img_0192d500',
  url: 'https://cdn.moonx.art/articles/0192f4d3/ring.jpg',
  widthPx: 1_600,
  heightPx: 1_067,
  byteSize: 288_310,
  contentType: 'image/jpeg',
};

/** A picture this desk stored, and the article and role it was claimed for. */
interface StoredPicture {
  readonly image: StoredImage;
  readonly articleId: string;
  readonly role: 'cover' | 'body';
}

/** A cover that is stored and is BELOW the ruled minimum, so `cover_dimensions` has a real red row. */
const SMALL_COVER: StoredImage = {
  fileId: 'img_0192d09e',
  url: 'https://cdn.moonx.art/articles/0192d09e/cover.jpg',
  widthPx: 640,
  heightPx: 360,
  byteSize: 141_880,
  contentType: 'image/jpeg',
};

/**
 * The platform's own test for an intake id, `UUID_SHAPE` (article-image-claim.service.ts): a
 * claim naming anything else is `UPLOAD_OBJECT_NOT_FOUND` before the bucket is read (:202-207).
 * Held here so the fixture cannot accept an id the network would refuse.
 */
const INTAKE_ID_SHAPE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * `awaitingCover`, derived as the platform derives it (`awaitingCover`,
 * working-copy.presenter.ts): no cover, or one with no measured size, is still waiting;
 * otherwise the policy's minimum decides - the minimum THIS desk serves, not a number written here.
 */
function awaitingCoverFor(cover: StoredImage | null): boolean {
  const rule = FIXTURE_STRUCTURAL_RULES.rules.find((row) => row.rule === 'cover_dimensions');
  const minWidth = rule?.minWidthPx ?? null;
  const minHeight = rule?.minHeightPx ?? null;
  if (cover === null) {
    return true;
  }
  const width = cover.widthPx ?? null;
  const height = cover.heightPx ?? null;
  if (width === null || height === null) {
    return true;
  }
  return (minWidth !== null && width < minWidth) || (minHeight !== null && height < minHeight);
}

const SATURN_BODY_ONE =
  'Everyone talks about the Saturn return as though it arrives on a fixed date with a letter of ' +
  'introduction. It does not. It arrives the way a landlord arrives: earlier than agreed, and ' +
  'interested chiefly in what you have been putting off. The planet takes a little under thirty ' +
  'years to come back to where it stood when you were born, and the two or three years around ' +
  'that homecoming have a reputation for rearranging a life.';

const SATURN_BODY_TWO =
  'What I want to argue here is narrower and less dramatic. The return does not do anything. It ' +
  'is a clock hand passing a mark. What it is good for is the question it makes obvious, which is ' +
  'whether the structures you built in your twenties are ones you would build again. Most people ' +
  'answer that question badly the first time, not because they are foolish, but because nobody ' +
  'asked them to answer it at all.';

const SATURN_QUOTE = 'Discipline is what remains when the enthusiasm has gone home.';

const SATURN_BLOCKS: readonly Block[] = [
  { blockId: '0192f4d3-0001-7a01-8a01-000000000001', kind: 'text', body: SATURN_BODY_ONE },
  {
    blockId: '0192f4d3-0001-7a02-8a01-000000000002',
    kind: 'quote',
    body: SATURN_QUOTE,
    quoteAttribution: 'A. Levit',
  },
  { blockId: '0192f4d3-0001-7a03-8a01-000000000003', kind: 'text', body: SATURN_BODY_TWO },
  {
    blockId: '0192f4d3-0001-7a04-8a01-000000000004',
    kind: 'image',
    fileId: 'img_0192d500',
    url: 'https://cdn.moonx.art/articles/0192f4d3/ring.jpg',
    imageAlt: 'Saturn photographed edge-on, its rings a thin bright line',
    caption: 'Edge-on, the rings almost vanish. Roughly every fifteen years.',
  },
  { blockId: '0192f4d3-0001-7a05-8a01-000000000005', kind: 'divider' },
];

/**
 * ★ THE SERVER'S COPY, for the conflict. It differs from `SATURN_BLOCKS` in exactly the way a
 * second tab differs: one paragraph was rewritten there and not here. The conflict pane's job is to
 * show BOTH and let the author choose, which is only demonstrable if they really differ.
 */
const SATURN_BLOCKS_SERVER: readonly Block[] = [
  { blockId: '0192f4d3-0001-7a01-8a01-000000000001', kind: 'text', body: SATURN_BODY_ONE },
  {
    blockId: '0192f4d3-0001-7a02-8a01-000000000002',
    kind: 'quote',
    body: SATURN_QUOTE,
    quoteAttribution: 'A. Levit',
  },
  {
    blockId: '0192f4d3-0001-7a03-8a01-000000000003',
    kind: 'text',
    body:
      SATURN_BODY_TWO +
      ' Edited in another tab at 11:04: the point is the question, not the planet, and the ' +
      'question is whether you would build it again.',
  },
  {
    blockId: '0192f4d3-0001-7a04-8a01-000000000004',
    kind: 'image',
    fileId: 'img_0192d500',
    url: 'https://cdn.moonx.art/articles/0192f4d3/ring.jpg',
    imageAlt: 'Saturn photographed edge-on, its rings a thin bright line',
    caption: 'Edge-on, the rings almost vanish. Roughly every fifteen years.',
  },
  { blockId: '0192f4d3-0001-7a05-8a01-000000000005', kind: 'divider' },
];

/**
 * ★ `wordCount` IS COMPUTED, NEVER TYPED IN. It is the server's own column, and the one function
 * that fills it is `shared/content-model`'s (`text-measure.ts`). A fixture that carried a
 * hand-written number would let the editor's mirror and the "server's" figure disagree by a token
 * and call that a defect in the screen.
 */
function withCounts(copy: Omit<WorkingCopy, 'wordCount' | 'blockCount'>): WorkingCopy {
  return { ...copy, wordCount: articleWordCount(copy), blockCount: copy.blocks.length };
}

function saturn(blocks: readonly Block[], revision: number): WorkingCopy {
  return withCounts({
    articleId: 'art_0192f4d3',
    status: 'changes_requested',
    revision,
    title: 'Saturn returns, and so does everyone else',
    subtitle: 'On the difference between a clock hand and a verdict',
    cover: COVER,
    seoTitle: 'Saturn return: what it actually asks of you',
    metaDescription:
      'The Saturn return is a clock hand passing a mark, not a verdict. What it is good for is the ' +
      'question it makes obvious.',
    slug: 'saturn-returns-and-so-does-everyone-else',
    sourceLanguage: 'en',
    articleFormat: 'essay',
    timeSensitivity: 'evergreen',
    plannedGoLiveAt: null,
    categories: ['astrology', 'selfKnowledge'],
    addressCategory: 'astrology',
    tags: ['saturn return', 'beginner guide'],
    blocks,
    awaitingCover: false,
    writableBy: 'author',
    updatedAt: '2026-08-23T17:05:00Z',
  });
}

const SHORT_DRAFT: WorkingCopy = withCounts({
  articleId: 'art_0192f3ff',
  status: 'draft',
  revision: 3,
  title: 'Untitled draft',
  subtitle: '',
  cover: SMALL_COVER,
  seoTitle: null,
  metaDescription: null,
  slug: null,
  sourceLanguage: 'en',
  articleFormat: null,
  timeSensitivity: null,
  plannedGoLiveAt: null,
  categories: [],
  addressCategory: null,
  tags: [],
  blocks: [
    {
      blockId: '0192f3ff-0001-7b01-9b01-000000000001',
      kind: 'text',
      body:
        'Starting this one before I lose the thought. The idea is that the September sky is quieter ' +
        'than August was, and that the quiet is the interesting part.',
    },
  ],
  // A cover IS stored and is below the ruled minimum, which is exactly what this derived flag
  // means: "no cover yet meets the ruled minimum", not "no cover at all".
  awaitingCover: true,
  writableBy: 'author',
  updatedAt: '2026-08-22T11:30:00Z',
});

/** In review: nobody may write. The screen opens read-only rather than refusing to open. */
const IN_REVIEW: WorkingCopy = withCounts({
  articleId: 'art_0192f5e2',
  status: 'in_review',
  revision: 11,
  title: 'On the ethics of prediction',
  subtitle: 'What you owe someone who believes you',
  cover: COVER,
  seoTitle: 'The ethics of prediction',
  metaDescription: 'What you owe a reader who believes you.',
  slug: 'on-the-ethics-of-prediction',
  sourceLanguage: 'en',
  articleFormat: 'essay',
  timeSensitivity: 'evergreen',
  plannedGoLiveAt: '2026-09-27T06:00:00Z',
  categories: ['astrology'],
  addressCategory: 'astrology',
  tags: ['ethics'],
  blocks: [
    { blockId: '0192f5e2-0001-7c01-ac01-000000000001', kind: 'text', body: SATURN_BODY_ONE },
    { blockId: '0192f5e2-0001-7c02-ac01-000000000002', kind: 'text', body: SATURN_BODY_TWO },
  ],
  awaitingCover: false,
  writableBy: 'nobody',
  updatedAt: '2026-08-24T09:41:00Z',
});

/**
 * Published: only an editor may write it (G19), so it opens as the read view - whose foot holds
 * "Archive this article" (design 24, T2). The list's row of the same id (fixtures.ts).
 */
const PUBLISHED: WorkingCopy = withCounts({
  articleId: 'art_0192f5c9',
  status: 'published',
  revision: 6,
  title: 'The eclipse nobody will photograph',
  subtitle: 'And why that is the best thing about it',
  cover: COVER,
  seoTitle: 'The eclipse nobody will photograph',
  metaDescription: 'Why the eclipse that happens out of sight is the best one to write about.',
  slug: 'the-eclipse-nobody-will-photograph',
  sourceLanguage: 'en',
  articleFormat: 'essay',
  timeSensitivity: 'evergreen',
  plannedGoLiveAt: null,
  categories: ['astrology'],
  addressCategory: 'astrology',
  tags: ['eclipse season'],
  blocks: [
    { blockId: '0192f5c9-0001-7d01-bd01-000000000001', kind: 'text', body: SATURN_BODY_ONE },
    { blockId: '0192f5c9-0001-7d02-bd01-000000000002', kind: 'text', body: SATURN_BODY_TWO },
  ],
  awaitingCover: false,
  writableBy: 'admin',
  updatedAt: '2026-08-26T06:00:00Z',
});

/* ---------------------------------------------------------------------------------------------
 * The source language, the stored versions and the author's profile.
 * ------------------------------------------------------------------------------------------- */

/**
 * The languages an article may be WRITTEN in - D24.1's four, exactly as the launch policy seeds
 * `translation_policy_source_language` (prisma/migrations/0009_launch_policy_versions), each with
 * the catalogue's `label_en` (0003_lookups_and_seeds). This desk SERVES the list the way
 * `listSourceLanguages` does (wave 26) and refuses any other code on a save with the platform's
 * uncoded 422 (`applySourceLanguage`, src/modules/articles/services/working-copy.repository.ts) -
 * one list for both, so the fixture cannot offer what it then refuses.
 */
const FIXTURE_SOURCE_LANGUAGE_LIST: SourceLanguageList = {
  items: [
    { code: 'en', name: 'English' },
    { code: 'ru', name: 'Russian' },
    { code: 'pl', name: 'Polish' },
    { code: 'es', name: 'Spanish' },
  ],
};
const FIXTURE_SOURCE_LANGUAGES: ReadonlySet<string> = new Set(
  FIXTURE_SOURCE_LANGUAGE_LIST.items.map((language) => language.code),
);

/** The fixed instant a version kept on this desk is stamped with. The desk reads no clock (C11). */
const KEPT_AT = '2026-08-23T18:04:00Z';

/**
 * A sealed snapshot of `copy`, as the platform's version writer stores one: the working copy's
 * members, its source language as the row's language, `sealed_at` set at the same instant.
 */
function sealedFrom(
  copy: WorkingCopy,
  revisionNo: number,
  reason: ArticleVersionSummary['reason'],
  createdAt: string,
): ArticleVersion {
  return {
    articleId: copy.articleId,
    revisionNo,
    role: 'source',
    languageCode: copy.sourceLanguage ?? 'en',
    sourceRevisionNo: null,
    reason,
    statusAt: copy.status,
    title: copy.title,
    subtitle: copy.subtitle,
    cover: copy.cover ?? null,
    seoTitle: copy.seoTitle ?? null,
    metaDescription: copy.metaDescription ?? null,
    articleFormat: copy.articleFormat ?? null,
    timeSensitivity: copy.timeSensitivity ?? null,
    categories: copy.categories ?? [],
    addressCategory: copy.addressCategory ?? null,
    tags: copy.tags ?? [],
    blocks: copy.blocks,
    wordCount: articleWordCount(copy),
    blockCount: copy.blocks.length,
    sealedAt: createdAt,
    createdAt,
  };
}

/** The list entry for a stored version - the same members, without the document. */
function summaryOf(version: ArticleVersion): ArticleVersionSummary {
  return {
    revisionNo: version.revisionNo,
    role: version.role,
    languageCode: version.languageCode,
    sourceRevisionNo: version.sourceRevisionNo ?? null,
    reason: version.reason ?? 'author_save',
    statusAt: version.statusAt ?? null,
    title: version.title,
    wordCount: version.wordCount,
    blockCount: version.blockCount,
    sealedAt: version.sealedAt ?? null,
    createdAt: version.createdAt,
  };
}

/**
 * Each article's versions, NEWEST FIRST as the list answers them. The Saturn piece was kept three
 * times as it grew and then sent to an editor - version 4 is the one its review is on (the
 * findings below say `revisionNo: 4`). The piece in review was sent once, and so was the published
 * one. The short draft has none, so its language is still the author's to choose and its History
 * pane is empty.
 */
function seededVersions(): Map<string, readonly ArticleVersion[]> {
  const whole = saturn(SATURN_BLOCKS, 7);
  const kept = (blocks: readonly Block[], title: string): WorkingCopy =>
    withCounts({ ...whole, status: 'draft', title, blocks });
  const [opening, quote, second, picture] = SATURN_BLOCKS;
  const grown = (count: number): readonly Block[] =>
    [opening, quote, second, picture].slice(0, count).filter((block) => block !== undefined);
  return new Map<string, readonly ArticleVersion[]>([
    [
      'art_0192f4d3',
      [
        sealedFrom({ ...whole, status: 'draft' }, 4, 'submit', '2026-08-22T14:00:00Z'),
        sealedFrom(kept(grown(4), whole.title), 3, 'author_save', '2026-08-21T08:45:00Z'),
        sealedFrom(kept(grown(3), whole.title), 2, 'author_save', '2026-08-20T21:10:00Z'),
        sealedFrom(kept(grown(1), 'Saturn returns'), 1, 'author_save', '2026-08-19T09:30:00Z'),
      ],
    ],
    [
      IN_REVIEW.articleId,
      [sealedFrom({ ...IN_REVIEW, status: 'draft' }, 1, 'submit', '2026-08-24T09:41:00Z')],
    ],
    [
      PUBLISHED.articleId,
      [sealedFrom({ ...PUBLISHED, status: 'draft' }, 1, 'submit', '2026-08-25T10:00:00Z')],
    ],
  ]);
}

/**
 * The author behind this desk - `FIXTURE_SESSION`'s name and address, so the byline beside the
 * draft and the cabinet's own session agree. No avatar, which is the card's initials case.
 */
const FIXTURE_PROFILE: OwnProfile = {
  authorId: FIXTURE_SESSION.authorId ?? null,
  displayName: FIXTURE_SESSION.displayName ?? null,
  slug: FIXTURE_SESSION.slug ?? null,
  slugFrozen: FIXTURE_SESSION.slugFrozen ?? false,
  avatarUrl: null,
  headline: 'Astrologer. Writes about the slow planets and what they ask of people.',
  visibility: { headline: true },
  expertise: [{ categoryCode: 'astrology', isPublic: true }],
  tags: [],
  links: [],
  signs: { birthDataOnFile: false, computedSignsPublic: false },
};

/* ---------------------------------------------------------------------------------------------
 * The review.
 *
 * FIVE FINDINGS AND NOT ONE SHAPE. Between them they cover all three anchor scopes, all three
 * anchor states, both blocking values, a suggested rewrite (a copy button, NEVER auto-applied) and
 * an already-resolved row — so every branch of the notes pane is rendered by the fixture rather
 * than reasoned about. NO NUMBER FROM THE MODERATION PIPELINE IS HERE: no score, no band, no
 * threshold, no per-finding deduction (authors-api.yaml).
 *
 * ★ AS A LIVE REVIEW SENDS THEM (design 28 A6-P1, A7): the review was decided by automatic means, so
 * each finding names a real reason (`taxonomyCode`) with its `label` and `authorGuidance` in the
 * words of docs/design/27-launch-texts/19-moderation-copy.md, and `note: null` - the checker's note
 * is for staff, and only a finding a person on the team wrote carries one.
 *
 * ★★ THE OFFSETS AND THE QUOTES ARE MEASURED AGAINST THE BLOCKS ABOVE, NOT INVENTED. Each of the
 * three span anchors below was computed over the real string, so `relocate` (anchors.ts)
 * returns the state each comment claims. A fixture whose numbers were plausible-looking would put
 * every span through the same fallback and leave the two interesting branches unrendered:
 *
 *   ordinal 1 — the offsets still hold the stored quote, so step 1 matches:  `position`
 *   ordinal 3 — the offsets are stale AND the context occurs exactly once:   `quote`
 *   ordinal 4 — the quote is not in the document at all:                     null (unanchored)
 * ------------------------------------------------------------------------------------------- */

const FINDINGS: readonly ReviewFinding[] = [
  {
    articleId: 'art_0192f4d3',
    revisionNo: 4,
    reviewSeq: 2,
    ordinal: 1,
    stage: 'quality',
    taxonomyCode: 'quality.hard_to_follow',
    label: 'Hard to follow',
    authorGuidance:
      'A passage is hard to understand on a first reading. Break the idea into smaller steps and ' +
      'explain them one by one.',
    severity: 'high',
    isBlocking: true,
    note: null,
    anchorScope: 'span',
    anchorState: 'position',
    blockId: '0192f4d3-0001-7a01-8a01-000000000001',
    // Code points into the block's plain text, inclusive start and exclusive end — measured over
    // SATURN_BODY_ONE, not guessed. The screen slices with `sliceCp` rather than by UTF-16 index.
    startCp: 238,
    endCp: 328,
    quoteExact:
      'The planet takes a little under thirty years to come back to where it stood when you were ',
    // At most 32 code points either side, which is the column's own bound.
    quotePrefix: 'what you have been putting off. ',
    quoteSuffix: 'born, and the two or three years',
    disposition: 'open',
  },
  {
    articleId: 'art_0192f4d3',
    revisionNo: 4,
    reviewSeq: 2,
    ordinal: 2,
    stage: 'quality',
    taxonomyCode: 'quality.generic_phrase',
    label: 'Vague generalization',
    authorGuidance:
      'Phrases such as "many believe" or "it is said" take the place of specifics. Say who, when ' +
      'or what exactly, or remove the sentence.',
    severity: 'medium',
    isBlocking: true,
    note: null,
    suggestedRewrite: 'Anna Levit',
    anchorScope: 'block',
    anchorState: null,
    blockId: '0192f4d3-0001-7a02-8a01-000000000002',
    disposition: 'open',
  },
  {
    articleId: 'art_0192f4d3',
    revisionNo: 4,
    reviewSeq: 2,
    ordinal: 3,
    stage: 'quality',
    taxonomyCode: 'quality.condescending',
    label: 'Talking down to readers',
    authorGuidance:
      'A passage talks down to readers. Write to them as adults who understand their own lives.',
    severity: 'low',
    isBlocking: false,
    note: null,
    anchorScope: 'span',
    /* ★ THE ANCHOR THAT MOVED. The server recorded `position` at the verdict; text was inserted
       above it since, so those offsets no longer hold the quote. The client re-runs the SAME
       ordered strategy the worker runs (anchors.ts:467: position → context → quote alone) and the
       context matches exactly once, so the highlight lands in the right place and the screen says
       it was relocated. This is the branch a client that trusted `anchorState` would get wrong. */
    anchorState: 'position',
    blockId: '0192f4d3-0001-7a03-8a01-000000000003',
    startCp: 216,
    endCp: 269,
    quoteExact: 'Most people answer that question badly the first time',
    quotePrefix: 'are ones you would build again. ',
    quoteSuffix: ', not because they are foolish, ',
    disposition: 'open',
  },
  {
    articleId: 'art_0192f4d3',
    revisionNo: 4,
    reviewSeq: 2,
    ordinal: 4,
    stage: 'quality',
    taxonomyCode: 'quality.padding_sentence',
    label: 'Filler sentence',
    authorGuidance:
      'A sentence gives readers no information. Remove it, or replace it with something they can use.',
    severity: 'low',
    isBlocking: false,
    note: null,
    anchorScope: 'span',
    /* ★ THE LOST ANCHOR, AND IT REALLY IS LOST: this quotation is in no block of this document, so
       every strategy fails and `relocate` answers null — "a second hit is a null, not a guess".
       THE FINDING KEEPS ITS QUOTE, which is what lets the author still see what was meant even
       though nothing can be highlighted (ReviewFinding.quoteExact, authors-api.yaml:5062-5064). */
    anchorState: 'unanchored',
    blockId: null,
    startCp: null,
    endCp: null,
    quoteExact: 'at the end of the day it is all just cycles',
    quotePrefix: null,
    quoteSuffix: null,
    disposition: 'open',
  },
  {
    articleId: 'art_0192f4d3',
    revisionNo: 4,
    reviewSeq: 2,
    ordinal: 5,
    stage: 'quality',
    taxonomyCode: 'quality.no_introduction',
    label: 'Missing introduction',
    authorGuidance:
      'The article starts without telling readers what it is about. Add a short opening that says ' +
      'what they will find in it.',
    severity: 'low',
    isBlocking: false,
    note: null,
    anchorScope: 'article',
    anchorState: null,
    disposition: 'resolved',
    resolvedAt: '2026-08-23T16:40:00Z',
  },
];

const EMPTY_REVIEW = (articleId: PublicId): ReviewRecord => ({
  articleId,
  reviewSeq: null,
  revisionNo: null,
  roundNo: null,
  outcome: null,
  automatedMeans: null,
  decidedAt: null,
  isOpen: false,
  openBlockingCount: 0,
  letter: null,
  findings: [],
});

/* ------------------------------------------------------------------------------------------- */

export function createFixtureEditorApi(options: FixtureEditorApiOptions = {}): EditorApi {
  const latency = options.latencyMs ?? DEFAULT_LATENCY_MS;
  const offline = options.offline === true;
  const streamAccess = options.streamAccess ?? 'granted';

  /* MUTABLE STATE, because a working copy is mutable and a revision counter is the whole point.
     One fixture instance is one author's desk for the life of the page. */
  const copies = new Map<string, WorkingCopy>([
    [SHORT_DRAFT.articleId, SHORT_DRAFT],
    ['art_0192f4d3', saturn(SATURN_BLOCKS, 7)],
    [IN_REVIEW.articleId, IN_REVIEW],
    [PUBLISHED.articleId, PUBLISHED],
    ...seededVideoCopies(),
  ]);
  /** The rules ticked on each video draft, as `setVideoDraftConfirmations` keeps them. */
  const ticked = new Map<PublicId, readonly VideoRule[]>();
  /** The videos behind the video drafts (./fixture-video-desk.ts). */
  const videos = createFixtureVideoDesk(
    options.videoFailure === undefined ? {} : { failWith: options.videoFailure },
  );
  /** ★ The second tab, armed once. See the header: the refusal path has to be reachable. */
  let conflictArmedFor: string | null = 'art_0192f4d3';
  const dispositions = new Map<number, ReviewFinding['disposition']>();
  /**
   * Every picture this desk has stored, by media id, WITH THE ARTICLE AND ROLE IT WAS CLAIMED FOR -
   * the platform accepts a `coverFileId` only for this article's own `cover` claim and an image
   * block's `fileId` only for its own `body` claim, one 404 for anything else
   * (`resolveClaimedImageKey`, src/modules/articles/services/working-copy.repository.ts). The two
   * seeded covers and the Saturn piece's one picture are registered to the drafts that show them.
   */
  const images = new Map<string, StoredPicture>([
    [COVER.fileId, { image: COVER, articleId: 'art_0192f4d3', role: 'cover' }],
    [SMALL_COVER.fileId, { image: SMALL_COVER, articleId: SHORT_DRAFT.articleId, role: 'cover' }],
    [SATURN_PICTURE.fileId, { image: SATURN_PICTURE, articleId: 'art_0192f4d3', role: 'body' }],
  ]);
  /**
   * Each article's calendar links as they are WRITTEN - pairs; the title and the start are joined
   * from the fixture calendar on every answer, as the platform joins `calendar_event`. The piece in
   * review is timed to equinox week, the week its go-live wish falls in.
   */
  const calendarLinks = new Map<string, readonly CalendarLinkPair[]>([
    [IN_REVIEW.articleId, [{ calendarEventId: 'cev_2026_09_equinox_week', relation: 'timed_to' }]],
  ]);
  /** Each article's group links (design 30), as codes; the piece in review is about the equinox. */
  const calendarGroups = new Map<string, readonly string[]>([
    [IN_REVIEW.articleId, ['autumn-equinox']],
  ]);
  /** Each article's stored versions, newest first. "Keep this version" adds one. */
  const versions = seededVersions();

  /**
   * The source language an autosave leaves stored - the platform's `applySourceLanguage`, in its
   * order: absent or unchanged keeps it; a language the policy does not offer is the uncoded 422;
   * so is a change once the article has a version or has left `draft`, because every version is
   * stored in the language it was written in and a move would orphan them.
   */
  function sourceLanguageAfter(copy: WorkingCopy, requested: string | null): string | null {
    const stored = copy.sourceLanguage ?? null;
    if (requested === null || requested === stored) {
      return stored;
    }
    if (!FIXTURE_SOURCE_LANGUAGES.has(requested)) {
      throw new ApiProblem(
        problem(
          422,
          'UNPROCESSABLE_UNSPECIFIED',
          'The request cannot be processed.',
          `"${requested}" is not a language an article may be written in`,
        ),
      );
    }
    if (copy.status !== 'draft' || (versions.get(copy.articleId) ?? []).length > 0) {
      throw new ApiProblem(
        problem(
          422,
          'UNPROCESSABLE_UNSPECIFIED',
          'The request cannot be processed.',
          'the source language is fixed once the article has been submitted for review',
        ),
      );
    }
    return requested;
  }

  /**
   * The address a save leaves, refused as the platform refuses it (R-019,
   * src/modules/articles/services/article-slug-reservation.ts): absent keeps the held one, `null`
   * gives it up, a string is reserved - unless it has no address category (422
   * `ADDRESS_CATEGORY_REQUIRED`) or another piece holds it (422 `SLUG_TAKEN`). An ever-published
   * piece is not writable here at all, so its 403 answers first, as the platform's does.
   */
  function slugAfter(copy: WorkingCopy, body: AutosaveRequest): string | null {
    const held = copy.slug ?? null;
    if (body.slug === undefined || body.slug === held) {
      return held;
    }
    if (body.slug === null) {
      return null;
    }
    if ((body.addressCategory ?? null) === null) {
      throw new ApiProblem(
        problem(
          422,
          'ADDRESS_CATEGORY_REQUIRED',
          'An address needs its category.',
          'slug: an address is a slug inside the address category; choose the address category first',
        ),
      );
    }
    if (body.slug === FIXTURE_TAKEN_SLUG) {
      throw new ApiProblem(
        problem(
          422,
          'SLUG_TAKEN',
          'That address is taken.',
          'slug: another article holds this address in the address category',
        ),
      );
    }
    return body.slug;
  }

  /**
   * The set as `listArticleCalendarEvents` answers it, in the platform's order: start, id,
   * relation; each event with its group's name, and the topics in the catalogue's order.
   */
  function linksOf(articleId: string): ArticleCalendarLinks {
    const groupName = (code: string): string | undefined =>
      FIXTURE_CALENDAR_GROUPS.find((group) => group.code === code)?.name;
    const links = (calendarLinks.get(articleId) ?? []).map((pair): ArticleCalendarLink => {
      const event = FIXTURE_CALENDAR_EVENTS.find(
        (row) => row.calendarEventId === pair.calendarEventId,
      );
      const code = event?.groupCode ?? null;
      const name = code === null ? undefined : groupName(code);
      return {
        ...pair,
        title: event?.title ?? null,
        startsAt: event?.startsAt ?? null,
        ...(code === null ? {} : { groupCode: code }),
        ...(name === undefined ? {} : { groupName: name }),
      };
    });
    links.sort(
      (a, b) =>
        (a.startsAt ?? '').localeCompare(b.startsAt ?? '') ||
        a.calendarEventId.localeCompare(b.calendarEventId) ||
        a.relation.localeCompare(b.relation),
    );
    const held = calendarGroups.get(articleId) ?? [];
    const groups = FIXTURE_CALENDAR_GROUPS.filter((group) => held.includes(group.code)).map(
      (group) => ({ groupCode: group.code, name: group.name }),
    );
    return { links, groups };
  }

  /**
   * The topic half of the write (design 30), checked as the platform checks it: strict members, at
   * most five, a set; a code the catalogue does not offer is the 404 unless the article holds it.
   */
  function groupCodesOf(
    sent: NonNullable<ArticleCalendarLinksWrite['groups']>,
    articleId: string,
  ): readonly string[] {
    if (sent.length > MAX_CALENDAR_GROUPS) {
      throw invalid(`at most ${String(MAX_CALENDAR_GROUPS)} groups may be sent.`);
    }
    const extra = sent.flatMap((group) => Object.keys(group)).find((key) => key !== 'groupCode');
    if (extra !== undefined) {
      throw invalid(`'${extra}' is not a member of an article calendar group link.`);
    }
    const codes = [...new Set(sent.map((group) => group.groupCode))];
    const held = calendarGroups.get(articleId) ?? [];
    const notLive = codes.find(
      (code) =>
        !held.includes(code) && !FIXTURE_CALENDAR_GROUPS.some((group) => group.code === code),
    );
    if (notLive !== undefined) {
      throw new ApiProblem(
        problem(
          404,
          'NOT_FOUND',
          'No such thing.',
          `groupCode '${notLive}' names no live calendar group.`,
        ),
      );
    }
    return codes;
  }

  /** The pipe's 400: no domain code means "validation failed", so it is the generic one. */
  function invalid(detail: string): ApiProblem {
    return new ApiProblem(
      problem(400, 'REQUEST_VALIDATION_FAILED', 'The request is not one this API accepts.', detail),
    );
  }

  /** The picture `fileId` names, if this article claimed it in this role - else the platform's 404. */
  function ownPicture(articleId: string, fileId: string, role: 'cover' | 'body'): StoredImage {
    const stored = images.get(fileId);
    if (stored === undefined || stored.articleId !== articleId || stored.role !== role) {
      throw new ApiProblem(problem(404, 'NOT_FOUND', 'No such file.', 'no such file'));
    }
    return stored.image;
  }

  async function settle<T>(value: T): Promise<T> {
    await new Promise((resolve) => setTimeout(resolve, latency));
    if (offline) {
      throw new TransportFailure('The request did not reach the platform.');
    }
    return value;
  }

  function copyOf(articleId: PublicId): WorkingCopy {
    const copy = copies.get(articleId) ?? newVideoDraft(articleId);
    if (copy === undefined) {
      // `Problem.error_code` has no not-found member, so a 404 on this API cannot carry one.
      throw new NotFoundFailure('That article is no longer there.');
    }
    return copy;
  }

  /** A draft the cabinet's desk made with "Upload video" or "Live stream", opened here as new. */
  function newVideoDraft(articleId: PublicId): WorkingCopy | undefined {
    if (!articleId.startsWith(FIXTURE_NEW_VIDEO_PREFIX)) {
      return undefined;
    }
    const draft = articleId.startsWith(FIXTURE_NEW_STREAM_PREFIX)
      ? emptyStreamDraft(articleId, '2026-08-23T17:00:00Z')
      : emptyVideoDraft(articleId, '2026-08-23T17:00:00Z');
    copies.set(articleId, draft);
    return draft;
  }

  /** A video draft answers with its video and its ticked rules, as `getOwnArticle` does. */
  function withVideo(copy: WorkingCopy): WorkingCopy {
    return copy.kind === 'video'
      ? {
          ...copy,
          video: videos.current(copy.articleId),
          videoConfirmations: [...(ticked.get(copy.articleId) ?? copy.videoConfirmations ?? [])],
        }
      : copy;
  }

  /** A video's own writes are allowed while its author may write the draft, as an autosave is. */
  function writableVideo(articleId: PublicId): WorkingCopy {
    const copy = copyOf(articleId);
    if (copy.kind !== 'video') {
      throw new ApiProblem(
        problem(422, 'UNPROCESSABLE_UNSPECIFIED', 'Not a video.', 'this article is not a video'),
      );
    }
    if (copy.writableBy !== 'author') {
      throw new ApiProblem(
        problem(
          403,
          'ARTICLE_NOT_WRITABLE',
          'This article is not yours to write just now.',
          `a video in "${copy.status}" is not writable by its author`,
        ),
      );
    }
    return copy;
  }

  /** A stream draft's writes and its submit need stream access (`requireAccess`, `accessRefusal`). */
  function refuseUnlessStreamAccess(copy: WorkingCopy): void {
    if (copy.stream === null || copy.stream === undefined || streamAccess === 'granted') {
      return;
    }
    throw new ApiProblem(
      problem(
        403,
        'STREAM_ACCESS_REQUIRED',
        'Live streams are not open to you yet.',
        'staff have not let this author announce live streams',
      ),
    );
  }

  /**
   * What a video's submit needs beyond an article's, in the platform's order (video-submit.ts): a
   * live stream's access, a ready video, then every rule of the guidelines confirmed - or, for a
   * live stream, its planned times with the start still ahead of the desk's now, and no rules.
   */
  function refuseVideoSubmit(copy: WorkingCopy, confirmed: readonly string[]): void {
    if (copy.kind !== 'video') {
      return;
    }
    refuseUnlessStreamAccess(copy);
    const video = videos.current(copy.articleId);
    if (video?.state !== 'ready') {
      throw new ApiProblem(
        problem(
          422,
          'VIDEO_REQUIRED',
          'The video is not ready.',
          video === null
            ? 'the video draft has no video'
            : `the video is ${video.state}, not ready`,
        ),
      );
    }
    if (copy.stream !== null && copy.stream !== undefined) {
      const startsAt = copy.stream.startsAt;
      if (startsAt === null || copy.stream.endsAt === null) {
        throw new ApiProblem(
          problem(
            422,
            'STREAM_TIMES_REQUIRED',
            'The live stream has no planned times.',
            'the live stream has no planned start and end',
          ),
        );
      }
      if (Date.parse(startsAt) <= Date.parse(DESK_NOW)) {
        throw new ApiProblem(
          problem(
            422,
            'STREAM_TIMES_REQUIRED',
            'The planned start has passed.',
            "the live stream's planned start has passed",
          ),
        );
      }
      return;
    }
    const missing = VIDEO_RULES.filter((rule) => !confirmed.includes(rule));
    if (missing.length > 0) {
      throw new ApiProblem(
        problem(
          422,
          'VIDEO_CONFIRMATIONS_REQUIRED',
          'Not every rule is confirmed.',
          `not confirmed: ${missing.join(', ')}`,
        ),
      );
    }
  }

  function findingsNow(): readonly ReviewFinding[] {
    return FINDINGS.map((finding) => {
      const moved = dispositions.get(finding.ordinal);
      return moved === undefined
        ? finding
        : { ...finding, disposition: moved, resolvedAt: '2026-08-23T18:00:00Z' };
    });
  }

  function openBlockingCount(): number {
    return findingsNow().filter((finding) => finding.isBlocking && finding.disposition === 'open')
      .length;
  }

  /**
   * The submit guard, run over the SAME numbers `getStructuralRules` served and the SAME word-count
   * and code-point functions the screen's checklist uses (./editor-measure.ts). One definition on
   * both sides of the call is what makes the checklist's red rows and this refusal the same set —
   * a fixture with its own arithmetic would make a disagreement look like a defect in the screen.
   */
  function refuseSubmit(copy: WorkingCopy): readonly StructuralLimitFailure[] {
    const failures: StructuralLimitFailure[] = [];
    const words = articleWordCount(copy);
    // A video has no word floor: its picture is the video.
    if (copy.kind !== 'video' && words < 300) {
      failures.push({ rule: 'word_count', limit: 300, actual: words });
    }
    const titleCp = countTitleLength(copy.title);
    if (titleCp < 10) {
      failures.push({ rule: 'title_length', limit: 10, actual: titleCp, field: 'title' });
    }
    const subtitleCp = countTitleLength(copy.subtitle);
    if (subtitleCp < 10) {
      failures.push({ rule: 'subtitle_length', limit: 10, actual: subtitleCp, field: 'subtitle' });
    }
    if ((copy.categories ?? []).length < 1) {
      failures.push({ rule: 'category_count', limit: 1, actual: 0 });
    }
    if (copy.awaitingCover === true) {
      // The cover the copy HOLDS - an autosave can change it now.
      failures.push({ rule: 'cover_dimensions', limit: 800, actual: copy.cover?.widthPx ?? 0 });
    }
    return failures;
  }

  return {
    // `async`, so an id this desk does not hold REJECTS: `useAsync` chains `.then().catch()` on the
    // promise, and a synchronous throw skipped that catch and crashed the editor route instead.
    getOwnArticle: async (articleId) => settle(withVideo(copyOf(articleId))),

    autosaveArticle: async (articleId: PublicId, body: AutosaveRequest) => {
      await new Promise((resolve) => setTimeout(resolve, latency));
      const copy = copyOf(articleId);

      if (copy.writableBy !== 'author') {
        // `ARTICLE_NOT_WRITABLE` and not `WRONG_FROM_STATE`: G19's rule is about WHO may write the
        // row at this status, which is a different refusal from an illegal transition.
        throw new ApiProblem(
          problem(
            403,
            'ARTICLE_NOT_WRITABLE',
            'This article is not yours to write just now.',
            'An article in review is written by nobody until a verdict is recorded.',
          ),
        );
      }

      if (conflictArmedFor === articleId) {
        conflictArmedFor = null;
        /* ★★ THE 409 CARRIES THE WHOLE WORKING COPY. Trimming it here would force the second
           round trip the contract removed, at the moment the author is closest to losing work. */
        const server = saturn(SATURN_BLOCKS_SERVER, copy.revision + 1);
        copies.set(articleId, server);
        throw new AutosaveConflict(
          problem(
            409,
            'AUTOSAVE_REVISION_STALE',
            'Another tab saved this draft first.',
            'The base revision you sent is behind the row. Your text is below, next to theirs.',
          ),
          server,
        );
      }

      // A video's description is text only (v1.0.29), refused before anything is written.
      if (copy.kind === 'video' && body.blocks.some((block) => block.kind === 'image')) {
        throw new ApiProblem(
          problem(
            422,
            'UNPROCESSABLE_UNSPECIFIED',
            'The request cannot be processed.',
            "a video's description has no pictures",
          ),
        );
      }

      /* ★ THE COVER IS RESOLVED, NOT ECHOED AND NOT IGNORED. The body is a whole-document save, so
         an absent id is no cover; an id this article did not claim AS ITS COVER is the platform's
         404 (`resolveCover`, article-editor.service.ts) rather than a cover quietly dropped - and
         so is an image block's id this article did not claim as a body picture. */
      const coverFileId = body.coverFileId ?? null;
      const cover = coverFileId === null ? null : ownPicture(articleId, coverFileId, 'cover');
      // Refused BEFORE anything is written: the platform's 422 rolls the whole save back.
      const sourceLanguage = sourceLanguageAfter(copy, body.sourceLanguage ?? null);
      const slug = slugAfter(copy, body);
      for (const block of body.blocks) {
        if (block.kind === 'image') {
          ownPicture(articleId, block.fileId, 'body');
        }
      }

      const saved: WorkingCopy = {
        ...copy,
        revision: copy.revision + 1,
        title: body.title,
        subtitle: body.subtitle,
        cover,
        awaitingCover: awaitingCoverFor(cover),
        seoTitle: body.seoTitle ?? null,
        metaDescription: body.metaDescription ?? null,
        sourceLanguage,
        slug,
        articleFormat: body.articleFormat ?? null,
        timeSensitivity: body.timeSensitivity ?? null,
        plannedGoLiveAt: body.plannedGoLiveAt ?? null,
        categories: body.categories ?? [],
        addressCategory: body.addressCategory ?? null,
        tags: body.tags ?? [],
        blocks: body.blocks,
        blockCount: body.blocks.length,
        updatedAt: '2026-08-23T18:02:00Z',
      };
      copies.set(articleId, saved);

      /* THE 200 DOES NOT ECHO `blocks`. The client already has them; what it does not have is the
         new revision (authors-api.yaml:1063-1072). */
      const result: WorkingCopySaved = {
        revision: saved.revision,
        updatedAt: saved.updatedAt,
        status: saved.status,
        blockCount: saved.blockCount,
        awaitingCover: saved.awaitingCover ?? false,
        slug: saved.slug ?? null,
        writableBy: saved.writableBy ?? null,
      };
      return result;
    },

    getStructuralRules: (_signal, query) =>
      settle(
        query?.kind === 'video' ? videoRulesOf(FIXTURE_STRUCTURAL_RULES) : FIXTURE_STRUCTURAL_RULES,
      ),
    listEditorCategories: () => settle(FIXTURE_CATEGORIES),

    // As the platform answers (design 24, T6): MoonX's own list only with nothing typed.
    suggestTags: (query) => {
      const typed = (query?.q ?? '').trim().toLowerCase();
      return settle(
        typed.length === 0
          ? { ...FIXTURE_TAGS, groups: FIXTURE_TAG_GROUPS }
          : { items: FIXTURE_TAGS.items.filter((tag) => tag.tagText.includes(typed)) },
      );
    },

    getPublishDateSuggestions: () => settle(FIXTURE_PUBLISH_DATES),

    checkArticleSlugAvailability: async (articleId, query) => {
      await settle(undefined);
      /* ★ EVERY REFUSAL THE PLATFORM MAKES, IN ITS ORDER (article-slug-cabinet.controller.ts): the
         query's shape is the pipe's 400; then one 422 for an article that is not this author's; the
         uncoded 422 for a category that does not exist; and `SLUG_FROZEN` once the address is. */
      if (query.slug.length < 3 || query.slug.length > 60 || !ARTICLE_SLUG_SHAPE.test(query.slug)) {
        throw invalid("'slug' is 3 to 60 lowercase letters, digits and single hyphens.");
      }
      const copy = copies.get(articleId);
      if (copy === undefined) {
        throw new ApiProblem(
          problem(
            422,
            'NOT_ARTICLE_OWNER',
            'Not your article.',
            'This article is not one of yours.',
          ),
        );
      }
      if (!FIXTURE_CATEGORIES.items.some((category) => category.code === query.categoryCode)) {
        throw new ApiProblem(
          problem(
            422,
            'UNPROCESSABLE_UNSPECIFIED',
            'The request cannot be processed.',
            'That category does not exist, so no address can be checked against it.',
          ),
        );
      }
      if (EVER_PUBLISHED.has(copy.status)) {
        throw new ApiProblem(
          problem(
            422,
            'SLUG_FROZEN',
            'The address is frozen.',
            'This article has been published, so its address can no longer change.',
          ),
        );
      }
      // The 200 is `{ available }` and nothing else — no reason and no suggestion.
      return { available: query.slug !== FIXTURE_TAKEN_SLUG };
    },

    claimArticleImageUpload: async (articleId: PublicId, body: ArticleImageClaim) => {
      await settle(undefined);
      // An article this desk does not hold is refused, as the platform's ownership check does.
      copyOf(articleId);
      if (!INTAKE_ID_SHAPE.test(body.intakeId)) {
        throw new ApiProblem(
          problem(
            404,
            'UPLOAD_OBJECT_NOT_FOUND',
            'There is no such object under your upload prefix.',
            'there is no such object under this caller’s quarantine prefix.',
          ),
        );
      }
      const stem = body.intakeId.slice(0, 8);
      const claimed: StoredImage = {
        // One intake id, one stored file: claiming the same object twice answers the same image.
        fileId: `img_${stem}`,
        url: `https://cdn.moonx.art/articles/claimed/${stem}.jpg`,
        widthPx: 2_048,
        heightPx: 1_152,
        byteSize: 402_110,
        contentType: 'image/jpeg',
      };
      images.set(claimed.fileId, { image: claimed, articleId, role: body.role });
      return claimed;
    },

    mintDraftPreviewLink: (articleId: PublicId) => {
      const link: PreviewLink = {
        url: `https://moonx.app/preview/${encodeURIComponent(articleId)}?k=fixture`,
        expiresAt: '2026-08-24T18:00:00Z',
      };
      return settle(link);
    },

    getArticleReview: (articleId: PublicId) => {
      if (articleId !== 'art_0192f4d3') {
        // An article with no review yet gets an EMPTY record rather than a 404, so the pane has an
        // empty state and never an error state.
        return settle(EMPTY_REVIEW(articleId));
      }
      const record: ReviewRecord = {
        articleId,
        reviewSeq: 2,
        revisionNo: 4,
        roundNo: 1,
        outcome: 'changes_requested',
        automatedMeans: true,
        decidedAt: '2026-08-23T16:02:00Z',
        isOpen: true,
        openBlockingCount: openBlockingCount(),
        letter:
          'Two things to fix before this goes back: the timing claim in the opening needs a source ' +
          'or a hedge, and the quote needs a full name. The two style notes are yours to take or ' +
          'leave.',
        findings: findingsNow(),
      };
      return settle(record);
    },

    resolveReviewFinding: async (articleId: PublicId, reviewSeq: number, ordinal: number) => {
      await new Promise((resolve) => setTimeout(resolve, latency));
      /* ★ THE PLATFORM'S ORDER AND CODES (`resolveFinding`, article-review.service.ts): the article
         is this author's; then the finding is on its review - else 404 `NOT_FOUND`; then the review
         is open and the finding still is - else 409 with NO domain code, `CONFLICT_UNSPECIFIED`.
         This desk's one review stays open, so only the "already dealt with" 409 has a case here.
         The 409 used to carry `WRONG_FROM_STATE`, which the platform never sends for this. */
      copyOf(articleId);
      const finding =
        articleId === 'art_0192f4d3'
          ? findingsNow().find((row) => row.reviewSeq === reviewSeq && row.ordinal === ordinal)
          : undefined;
      if (finding === undefined) {
        throw new ApiProblem(
          problem(404, 'NOT_FOUND', 'No such thing.', 'no such review or finding on this article'),
        );
      }
      if (finding.disposition !== 'open') {
        throw new ApiProblem(
          problem(
            409,
            'CONFLICT_UNSPECIFIED',
            'That conflicts with the current state.',
            `this finding is already ${finding.disposition}`,
          ),
        );
      }
      dispositions.set(ordinal, 'resolved');
      const resolved: FindingResolved = {
        finding: { ...finding, disposition: 'resolved', resolvedAt: '2026-08-23T18:00:00Z' },
        // ★ THE COUNT THE RESUBMIT BUTTON IS GATED ON, on this very response.
        openBlockingCount: openBlockingCount(),
        reviewIsOpen: true,
      };
      return resolved;
    },

    submitArticle: async (articleId: PublicId, body) => {
      await new Promise((resolve) => setTimeout(resolve, latency));
      const copy = copyOf(articleId);
      refuseVideoSubmit(copy, body?.videoConfirmations ?? []);
      if (copy.status !== 'draft') {
        throw new ApiProblem(problem(409, 'WRONG_FROM_STATE', 'Only a draft can be submitted.'));
      }
      // AS THE PLATFORM DOES (design 14, F8): a missing address category is its own plain 422,
      // answered BEFORE the structural set - not a `failures` row. The editor's own checklist row
      // (./../../../routes/articles/editor/address.ts) is where the author is told beforehand.
      if (copy.addressCategory === null || copy.addressCategory === undefined) {
        throw new ApiProblem(
          problem(
            422,
            'ADDRESS_CATEGORY_REQUIRED',
            'Address category required',
            'choose which of the categories this article is filed under becomes part of its permanent web address',
          ),
        );
      }
      const failures = refuseSubmit(copy);
      if (failures.length > 0) {
        // THE WHOLE SET, never the first failure alone.
        throw new StructuralLimitsRefusal(
          problem(
            422,
            'STRUCTURAL_LIMITS_FAILED',
            'This article is not ready to submit for review yet.',
          ),
          failures,
        );
      }
      const moved: TransitionResult = {
        articleId,
        fromStatus: 'draft',
        status: 'in_review',
        seq: 4,
        revisionNo: 5,
        occurredAt: '2026-08-23T18:05:00Z',
        queuedForReview: true,
      };
      // A video goes to a person and reads back in review, as the platform answers it next.
      if (copy.kind === 'video') {
        copies.set(articleId, { ...copy, status: 'in_review', writableBy: 'nobody' });
      }
      return moved;
    },

    resubmitArticle: async (articleId: PublicId, body) => {
      await new Promise((resolve) => setTimeout(resolve, latency));
      const copy = copyOf(articleId);
      refuseVideoSubmit(copy, body?.videoConfirmations ?? []);
      if (copy.status !== 'changes_requested') {
        throw new ApiProblem(
          problem(409, 'WRONG_FROM_STATE', 'Only an article needing changes can be resubmitted.'),
        );
      }
      if (openBlockingCount() > 0) {
        throw new ApiProblem(
          problem(
            409,
            'BLOCKING_ISSUES_UNRESOLVED',
            'Some notes still have to be marked as dealt with.',
            `${String(openBlockingCount())} blocking note(s) are still open on this review.`,
          ),
        );
      }
      const moved: TransitionResult = {
        articleId,
        fromStatus: 'changes_requested',
        status: 'in_review',
        seq: 9,
        revisionNo: 5,
        occurredAt: '2026-08-23T18:10:00Z',
        queuedForReview: true,
      };
      return moved;
    },

    // `async`, so an article this desk does not hold REJECTS rather than throwing synchronously.
    listArticleCalendarEvents: async (articleId: PublicId) => {
      copyOf(articleId);
      return settle(linksOf(articleId));
    },

    setArticleCalendarEvents: async (articleId: PublicId, body: ArticleCalendarLinksWrite) => {
      await settle(undefined);
      copyOf(articleId);
      if (body.links.length > MAX_CALENDAR_LINKS) {
        throw invalid(`at most ${String(MAX_CALENDAR_LINKS)} links may be sent.`);
      }
      for (const link of body.links) {
        // THE WRITE SCHEMA IS STRICT: a link echoed with its read-only `title` or `startsAt` is a
        // 400 naming the member (calendar-cabinet.schemas.ts), so this desk is no kinder.
        const extra = Object.keys(link).find(
          (key) => key !== 'calendarEventId' && key !== 'relation',
        );
        if (extra !== undefined) {
          throw invalid(`'${extra}' is not a member of an article calendar link.`);
        }
      }
      // A SET, so a repeated pair is one link - deduped and never refused.
      const pairs: CalendarLinkPair[] = [];
      for (const link of body.links) {
        if (
          !pairs.some(
            (p) => p.calendarEventId === link.calendarEventId && p.relation === link.relation,
          )
        ) {
          pairs.push({ calendarEventId: link.calendarEventId, relation: link.relation });
        }
      }
      const badRelation = pairs.find((pair) => !FIXTURE_LINK_RELATIONS.has(pair.relation));
      if (badRelation !== undefined) {
        throw invalid(`'${badRelation.relation}' is not a calendar link relation.`);
      }
      const notLive = pairs.find(
        (pair) =>
          !FIXTURE_CALENDAR_EVENTS.some((row) => row.calendarEventId === pair.calendarEventId),
      );
      if (notLive !== undefined) {
        // The contract's own 404: "one of the `calendarEventId` values names no live event".
        throw new ApiProblem(
          problem(
            404,
            'NOT_FOUND',
            'No such thing.',
            `calendarEventId '${notLive.calendarEventId}' names no live calendar event.`,
          ),
        );
      }
      const groups = body.groups === undefined ? undefined : groupCodesOf(body.groups, articleId);
      calendarLinks.set(articleId, pairs);
      if (groups !== undefined) {
        calendarGroups.set(articleId, groups);
      }
      return linksOf(articleId);
    },

    listArticleVersions: async (articleId: PublicId) => {
      copyOf(articleId);
      return settle({ items: (versions.get(articleId) ?? []).map(summaryOf) });
    },

    getArticleVersion: async (articleId: PublicId, revisionNo: number, query) => {
      await settle(undefined);
      copyOf(articleId);
      // No `language` is "the source-language row"; one names a language row of that revision.
      const language = query?.language;
      const found = (versions.get(articleId) ?? []).find(
        (version) =>
          version.revisionNo === revisionNo &&
          (language === undefined ? version.role === 'source' : version.languageCode === language),
      );
      if (found === undefined) {
        throw new ApiProblem(
          problem(
            404,
            'NOT_FOUND',
            'No such thing.',
            'no such revision, or no row for that language',
          ),
        );
      }
      return found;
    },

    saveArticleVersion: async (articleId: PublicId) => {
      await settle(undefined);
      const copy = copyOf(articleId);
      if (copy.writableBy !== 'author') {
        // The autosave's own 403: a version is a snapshot of what the autosave writes.
        throw new ApiProblem(
          problem(
            403,
            'ARTICLE_NOT_WRITABLE',
            'This article is not yours to write just now.',
            `an article in "${copy.status}" is not writable by its author`,
          ),
        );
      }
      const stored = versions.get(articleId) ?? [];
      // The next number off the article's own counter, so two presses are two versions, in order.
      const next =
        stored.reduce((highest, version) => Math.max(highest, version.revisionNo), 0) + 1;
      const sealed = sealedFrom(copy, next, 'author_save', KEPT_AT);
      versions.set(articleId, [sealed, ...stored]);
      return summaryOf(sealed);
    },

    getOwnProfile: () => settle(FIXTURE_PROFILE),

    listSourceLanguages: () => settle(FIXTURE_SOURCE_LANGUAGE_LIST),

    // A video's video (./fixture-video-desk.ts). `async`, so a refusal rejects.
    getArticleVideo: async (articleId: PublicId) => {
      await settle(undefined);
      copyOf(articleId);
      return videos.read(articleId);
    },

    setArticleVideo: async (articleId: PublicId, body) => {
      await settle(undefined);
      const copy = writableVideo(articleId);
      refuseUnlessStreamAccess(copy);
      // A live stream is a YouTube link, never an upload (as `ArticleVideoService.set`).
      if (copy.stream !== null && copy.stream !== undefined && body.source !== 'youtube') {
        throw new ApiProblem(
          problem(
            422,
            'UNPROCESSABLE_UNSPECIFIED',
            'Not an upload.',
            'a live stream is a YouTube link, not an upload',
          ),
        );
      }
      return videos.set(articleId, body);
    },

    removeArticleVideo: async (articleId: PublicId) => {
      await settle(undefined);
      writableVideo(articleId);
      videos.remove(articleId);
    },

    setVideoCover: async (articleId: PublicId, body) => {
      await settle(undefined);
      writableVideo(articleId);
      const image = videos.cover(articleId, body);
      // Stored as this article's cover, so the autosave that names it is accepted.
      images.set(image.fileId, { image, articleId, role: 'cover' });
      return image;
    },

    setVideoDraftConfirmations: async (articleId: PublicId, body) => {
      await settle(undefined);
      writableVideo(articleId);
      // In the guidelines' order, as the server answers.
      const rules = VIDEO_RULES.filter((rule) => body.rules.includes(rule));
      ticked.set(articleId, rules);
      return { rules };
    },

    // A live stream's planned times (1.2.0), refused in the platform's order: the request's own 400
    // (`streamTimesRequest`: the end after the start, at most 24 hours later), then the draft
    // (`setStreamTimes`): writable, a stream, and stream access.
    setStreamTimes: async (articleId: PublicId, body) => {
      await settle(undefined);
      const length = Date.parse(body.endsAt) - Date.parse(body.startsAt);
      if (!(length > 0 && length <= STREAM_LONGEST_MS)) {
        throw new ApiProblem(
          problem(
            400,
            'REQUEST_VALIDATION_FAILED',
            'Invalid request.',
            'endsAt: endsAt must be after startsAt, at most 24 hours later',
          ),
        );
      }
      const copy = writableVideo(articleId);
      if (copy.stream === null || copy.stream === undefined) {
        throw new ApiProblem(
          problem(
            422,
            'UNPROCESSABLE_UNSPECIFIED',
            'Not a live stream.',
            'this video is not a live stream',
          ),
        );
      }
      refuseUnlessStreamAccess(copy);
      const times = { startsAt: body.startsAt, endsAt: body.endsAt };
      copies.set(articleId, { ...copy, stream: times });
      return times;
    },
  };
}
